import { useEffect, useMemo } from 'react'

import { fetchOwnerContexts } from '../../hooks/useApi'
import {
    useContextsStore,
    selectContextsAreViewersOwn,
} from '../../stores/useContextsStore'
import {
    useOnboardingStore,
    GUIDE_SIGNALS,
} from '../../stores/useOnboardingStore'
import { STAGES, PERSONAS, getStageFlow } from './copy'

// How often to look for the graph the agent is building while this tab is
// visible. One list fetch; the agent's reply usually brings the user back
// before it fires.
const AGENT_GRAPH_POLL_MS = 20000

/**
 * The stages that complete off the graph page: `connect` (an API key now
 * exists, as ApiTokenManager reports it) and the builder's `bring` (a graph
 * built from the agent over MCP has appeared in the user's list). Both run on
 * the service pages (/apps, /api-access); the graph-page observers are
 * useGuideObservers.
 */
export default function useOffGraphObservers({ enabled }) {
    const stage = useOnboardingStore(state => state.stage)
    const persona = useOnboardingStore(state => state.persona)
    const apiKeyCount = useOnboardingStore(state => state.apiKeyCount)
    const contexts = useContextsStore(state => state.contexts)
    const contextsAreViewersOwn = useContextsStore(selectContextsAreViewersOwn)

    useEffect(() => {
        if (!enabled || stage !== STAGES.CONNECT) return
        if (!(apiKeyCount > 0)) return
        const flow = getStageFlow(persona)
        const index = flow.indexOf(stage)
        useOnboardingStore
            .getState()
            .advanceTo(flow[Math.min(index + 1, flow.length - 1)], {
                advancedBy: 'observed',
            })
    }, [enabled, stage, persona, apiKeyCount])

    const watchingForAgentGraph =
        enabled && persona === PERSONAS.BUILDER && stage === STAGES.BRING

    // The list is fetched once at boot; the agent writes to it from outside
    // this tab, so refresh it whenever the user comes back here, and now and
    // then while they wait.
    useEffect(() => {
        if (!watchingForAgentGraph) return
        let cancelled = false
        const refresh = () => {
            if (document.visibilityState === 'hidden') return
            fetchOwnerContexts()
                .then(response => {
                    if (cancelled || !response || response.accessDenied) return
                    if (useContextsStore.getState().currentGraphOwner) return
                    useContextsStore
                        .getState()
                        .setContexts(response.contextRecords || [])
                })
                .catch(() => {
                    // Next focus or tick tries again.
                })
        }
        refresh()
        window.addEventListener('focus', refresh)
        document.addEventListener('visibilitychange', refresh)
        const timer = window.setInterval(refresh, AGENT_GRAPH_POLL_MS)
        return () => {
            cancelled = true
            window.removeEventListener('focus', refresh)
            document.removeEventListener('visibilitychange', refresh)
            window.clearInterval(timer)
        }
    }, [watchingForAgentGraph])

    const agentGraph = useMemo(() => {
        if (!watchingForAgentGraph || !contextsAreViewersOwn) return null
        if (!contexts.length) return null
        // Newest first when the list says when; the last record otherwise.
        const dated = contexts.filter(context => context.createdAt)
        if (dated.length) {
            return dated.reduce((newest, context) =>
                new Date(context.createdAt) > new Date(newest.createdAt)
                    ? context
                    : newest
            )
        }
        return contexts[contexts.length - 1]
    }, [watchingForAgentGraph, contextsAreViewersOwn, contexts])

    useEffect(() => {
        if (!agentGraph) return
        useOnboardingStore
            .getState()
            .recordSignal(GUIDE_SIGNALS.AGENT_GRAPH_APPEARED)
    }, [agentGraph])

    return { agentGraph }
}
